import React, { useState, useEffect, useRef, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { aniversarioAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import UserAvatar from './UserAvatar';
import { X, Send, PartyPopper, Cake, Heart, Star, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const AniversarioCelebration = () => {
  const { user } = useAuth();
  const [aniversariantes, setAniversariantes] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [show, setShow] = useState(false);
  const [mensagens, setMensagens] = useState([]);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [showMural, setShowMural] = useState(false);
  const confettiRef = useRef(false);

  useEffect(() => {
    checkAniversarios();
  }, []);

  const checkAniversarios = async () => {
    try {
      const response = await aniversarioAPI.hoje();
      const lista = response.data.aniversariantes || [];
      if (lista.length > 0) {
        setAniversariantes(lista);
        // Verificar se já viu hoje
        const key = `aniversario_visto_${new Date().toISOString().split('T')[0]}`;
        const visto = localStorage.getItem(key);
        if (!visto) {
          setShow(true);
          localStorage.setItem(key, 'true');
        }
      }
    } catch (error) {
      console.error('Erro ao verificar aniversários:', error);
    }
  };

  const fireConfetti = useCallback(() => {
    if (confettiRef.current) return;
    confettiRef.current = true;

    const duration = 4000;
    const end = Date.now() + duration;
    const colors = ['#dc2626', '#7c3aed', '#f59e0b', '#10b981', '#ec4899', '#3b82f6'];

    const frame = () => {
      confetti({
        particleCount: 4,
        angle: 60,
        spread: 65,
        origin: { x: 0, y: 0.6 },
        colors,
      });
      confetti({
        particleCount: 4,
        angle: 120,
        spread: 65,
        origin: { x: 1, y: 0.6 },
        colors,
      });
      if (Date.now() < end) requestAnimationFrame(frame);
      else confettiRef.current = false;
    };
    frame();

    // Big burst
    setTimeout(() => {
      confetti({ particleCount: 120, spread: 100, origin: { y: 0.5 }, colors });
    }, 300);
    setTimeout(() => {
      confetti({ particleCount: 80, spread: 120, origin: { y: 0.4 }, colors, shapes: ['star'] });
    }, 800);
  }, []);

  useEffect(() => {
    if (show) {
      fireConfetti();
      loadMensagens();
    }
  }, [show, currentIdx, fireConfetti]);

  const loadMensagens = async () => {
    if (!aniversariantes[currentIdx]) return;
    try {
      const resp = await aniversarioAPI.mensagens(aniversariantes[currentIdx].id);
      setMensagens(resp.data.mensagens || []);
    } catch { /* ignore */ }
  };

  const enviarParabens = async () => {
    if (!novaMensagem.trim()) return;
    setEnviando(true);
    try {
      const resp = await aniversarioAPI.enviarMensagem(aniversariantes[currentIdx].id, novaMensagem.trim());
      setMensagens(prev => [resp.data.mensagem, ...prev]);
      setNovaMensagem('');
      toast.success('Parabéns enviado!');
      fireConfetti();
    } catch {
      toast.error('Erro ao enviar');
    } finally {
      setEnviando(false);
    }
  };

  const aniv = aniversariantes[currentIdx];

  if (!aniversariantes.length) return null;

  // Mini badge no header (sempre visível se tem aniversariante)
  if (!show) {
    return (
      <button
        onClick={() => setShow(true)}
        className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-red-600 via-purple-600 to-red-600 text-white px-4 py-3 rounded-2xl shadow-2xl hover:scale-105 transition-transform flex items-center gap-2 animate-bounce"
        data-testid="btn-aniversario-badge"
        style={{ animationDuration: '2s' }}
      >
        <Cake className="h-5 w-5" />
        <span className="font-bold text-sm">Aniversário de {aniversariantes.map(a => a.nome.split(' ')[0]).join(', ')}!</span>
        <PartyPopper className="h-5 w-5" />
      </button>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="aniversario-modal">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShow(false)} />

      {/* Card principal */}
      <div className="relative z-10 w-full max-w-lg mx-4 animate-in fade-in zoom-in duration-500">
        {/* Banner com foto */}
        <div className="relative bg-gradient-to-br from-red-600 via-purple-700 to-red-800 rounded-t-3xl overflow-hidden p-8 text-center">
          {/* Padrão decorativo */}
          <div className="absolute inset-0 opacity-10">
            {Array.from({ length: 20 }).map((_, i) => (
              <Star key={i} className="absolute text-yellow-300" style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                width: `${12 + Math.random() * 16}px`,
                opacity: 0.3 + Math.random() * 0.7,
                transform: `rotate(${Math.random() * 360}deg)`,
              }} />
            ))}
          </div>

          {/* Fechar */}
          <button onClick={() => setShow(false)} className="absolute top-3 right-3 text-white/60 hover:text-white z-10 p-1" data-testid="fechar-aniversario">
            <X className="h-6 w-6" />
          </button>

          {/* Navegação múltiplos aniversariantes */}
          {aniversariantes.length > 1 && (
            <div className="absolute top-3 left-3 flex gap-1 z-10">
              {aniversariantes.map((_, i) => (
                <button key={i} onClick={() => setCurrentIdx(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentIdx ? 'bg-white w-6' : 'bg-white/40 hover:bg-white/70'}`}
                />
              ))}
            </div>
          )}

          {/* Foto do aniversariante */}
          <div className="relative mx-auto mb-4">
            <div className="w-28 h-28 mx-auto rounded-full border-4 border-white/30 shadow-2xl overflow-hidden bg-white/10 flex items-center justify-center">
              <UserAvatar fotoPath={aniv?.foto_path} nome={aniv?.nome} size="xl" className="!w-full !h-full !rounded-full" />
            </div>
            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-yellow-400 text-yellow-900 px-3 py-0.5 rounded-full text-xs font-bold shadow-lg">
              <Cake className="inline h-3 w-3 mr-1" />ANIVERSÁRIO
            </div>
          </div>

          {/* Mensagem */}
          <h2 className="text-3xl font-black text-white drop-shadow-lg mb-1">
            Feliz Aniversário!
          </h2>
          <p className="text-xl font-bold text-white/90 mb-1">{aniv?.nome}</p>
          <p className="text-white/60 text-sm">{aniv?.perfil}</p>
        </div>

        {/* Mural de mensagens */}
        <div className="bg-white dark:bg-[#1a0a2e] rounded-b-3xl max-h-[50vh] overflow-hidden flex flex-col">
          {/* Toggle mural */}
          <button
            onClick={() => setShowMural(!showMural)}
            className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold text-purple-600 dark:text-purple-300 hover:bg-gray-50 dark:hover:bg-purple-900/20 transition-colors border-b border-gray-100 dark:border-purple-900/30"
            data-testid="toggle-mural"
          >
            <MessageCircle className="h-4 w-4" />
            Mural de Parabéns ({mensagens.length})
            <PartyPopper className="h-4 w-4" />
          </button>

          {showMural && (
            <>
              {/* Input de mensagem */}
              <div className="p-4 border-b border-gray-100 dark:border-purple-900/30">
                <div className="flex gap-2">
                  <Textarea
                    value={novaMensagem}
                    onChange={(e) => setNovaMensagem(e.target.value)}
                    placeholder="Deixe seus parabéns..."
                    className="flex-1 resize-none text-sm"
                    rows={2}
                    data-testid="input-parabens"
                  />
                  <Button
                    onClick={enviarParabens}
                    disabled={enviando || !novaMensagem.trim()}
                    className="bg-gradient-to-r from-red-600 to-purple-600 hover:brightness-110 self-end"
                    data-testid="btn-enviar-parabens"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Lista de mensagens */}
              <div className="overflow-y-auto max-h-48 p-4 space-y-3">
                {mensagens.length === 0 ? (
                  <p className="text-center text-gray-400 dark:text-purple-400/50 text-sm py-4">
                    Seja o primeiro a dar parabéns! 🎉
                  </p>
                ) : (
                  mensagens.map((m, i) => (
                    <div key={i} className="flex gap-3 p-3 bg-gray-50 dark:bg-purple-900/20 rounded-xl">
                      <div className="shrink-0">
                        <UserAvatar fotoPath={m.autor_foto} nome={m.autor_nome} size="sm" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm text-gray-800 dark:text-white">{m.autor_nome}</span>
                          <Heart className="h-3 w-3 text-red-500 fill-red-500" />
                        </div>
                        <p className="text-sm text-gray-600 dark:text-purple-200/70 mt-0.5">{m.mensagem}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          {/* Botão de fechar no final */}
          {!showMural && (
            <div className="p-4 flex justify-center">
              <Button
                onClick={() => { fireConfetti(); toast('🎉 Parabéns enviado!'); }}
                className="bg-gradient-to-r from-red-600 to-purple-600 hover:brightness-110 px-8"
                data-testid="btn-parabens-rapido"
              >
                <PartyPopper className="h-4 w-4 mr-2" />
                Dar Parabéns!
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AniversarioCelebration;
